import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketIcon } from '@phosphor-icons/react/dist/csr/Ticket';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { FilePdfIcon } from '@phosphor-icons/react/dist/csr/FilePdf';
import { LockSimpleIcon } from '@phosphor-icons/react/dist/csr/LockSimple';
import { LockSimpleOpenIcon } from '@phosphor-icons/react/dist/csr/LockSimpleOpen';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { CaseEscalations } from './CaseEscalations';
import { CaseMedia } from './CaseMedia';
import { CaseReplacements } from './CaseReplacements';
import { CaseMessages } from './CaseMessages';
import { CaseRequests } from './CaseRequests';
import { CaseUpdateDialog } from './CaseUpdateDialog';
import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { ApiError } from '../../lib/api/client';
import { casesApi, type CaseRow } from '../../lib/api/cases';
import { saveCasePdf } from '../../lib/api/exports';
import { canReadEscalations } from '../../lib/permissions/escalations';
import { canReadReplacements } from '../../lib/permissions/replacements';
import { statusLabelKey } from './caseStatusLabel';
import styles from './CaseDetail.module.css';
import { optionalReferenceName } from '../../lib/format/referenceName';

/**
 * The case detail view — the record, its claimed codes, and the
 * edit/close controls, each gated on its own permission and each HIDDEN
 * once the case is closed (`CaseDetail.test.tsx`'s own proof: a closed case
 * offers neither, regardless of which permissions the session holds).
 *
 * ## The PDF download is neither permission- nor status-gated (M-15c fix
 * round, Finding 1)
 *
 * `GET /cases/{uuid}/pdf` is gated on `case.view` alone, with no
 * closed-case restriction — unlike `update()`/`close()`, which both `409`
 * once a case is closed, which is why THOSE controls disappear here. A
 * closed case is exactly the one most likely to be downloaded (the archived
 * record for a physical file, or to send to Abbott), so the download button
 * renders for every case this screen can show at all, regardless of
 * `isClosed` or of `canUpdate`/`canClose`.
 *
 * It also has no busy screen of its own to reuse — it renders in-flight
 * state locally (`pdfBusy`), following `ExportButton.tsx`'s idiom
 * (`loading`, not `disabled`; the busy flag always comes back in a
 * `finally`) rather than inventing a second version of that pattern.
 *
 * ## Claimed codes come from the fetched case itself (M-06 fix round, gap 2)
 *
 * `GET /cases/{case}` now reports `code_claims` on `CaseResource` itself
 * (verified against the API repository AFTER this fix) — this screen reads
 * it straight off `record.code_claims`, never a session-scoped prop. Every
 * case this screen opens reports its claimed codes this way, not only the
 * one this browser session happened to create, which is what let the
 * previous "codes unavailable" notice go away entirely: there is no case
 * left for which claimed codes cannot be shown.
 *
 * ## `code_claims === undefined` is NOT the same as an empty list (final
 * whole-branch review, Finding 2)
 *
 * `record` is replaced WHOLESALE by whatever `casesApi.update()`/`close()`
 * resolve to (`setRecord(updated)` below) — the API repository now loads
 * `codes` on both those responses too, so `code_claims` should always be
 * present after an edit or a close for a case that has ever claimed one.
 * Before that API fix, `PATCH`/`close` responses carried no `code_claims`
 * key at all, and this screen treated that ABSENCE identically to an EMPTY
 * list — closing a case with a claimed voucher flipped the section straight
 * to "no codes claimed" on screen, for a case that still had one; a reload
 * (which re-fetches `GET /cases/{case}`, which always loaded `codes`)
 * silently restored it. The render below now treats `undefined` as "nothing
 * to show yet" (renders neither the list nor the empty notice) — belt and
 * braces alongside the API fix, so a future response that once again omits
 * `code_claims` reads as "unknown", never as "confirmed empty".
 */

export interface CaseDetailProps {
  uuid: string;
  permissions: readonly string[];
  onBack: () => void;
  /** Passed straight through to `CaseRequests` — see its own prop docblock. */
  onOpenRequest?: (uuid: string) => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function CaseDetail({ uuid, permissions, onBack, onOpenRequest }: CaseDetailProps) {
  const { t, i18n } = useTranslation();
  const canUpdate = permissions.includes('case.update');
  const canClose = permissions.includes('case.close');
  // Administrator-only server-side (`CasePolicy::delete()`), and deliberately
  // NOT implied by `case.update` — see that policy's own docblock. The gate
  // is the server's; this only decides whether to draw the control.
  const canDelete = permissions.includes('case.delete');
  // Administrator-only server-side (`CasePolicy::reopen()`), and deliberately
  // NOT implied by `case.close`: four roles may end a case, and putting a
  // finished one back into every queue and open-case count is a different
  // grant. The gate is the server's; this only decides whether to draw it.
  const canReopen = permissions.includes('case.reopen');
  const canSend = permissions.includes('sms.send');
  const canViewRequests = permissions.includes('request.view');
  const canViewEscalations = canReadEscalations(permissions);
  // DERIVED BY THE SHARED HELPER, never re-implemented here — see
  // `src/lib/permissions/replacements.ts`'s own docblock. Two copies of
  // one gating rule is how `escalation.view` hid a feature three times in
  // one milestone.
  const canViewReplacements = canReadReplacements(permissions);

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [record, setRecord] = useState<CaseRow | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenFailure, setReopenFailure] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  /*
   * The serials typed into the close dialog. Held here rather than in the
   * dialog because the dialog is a render of this component, and reset
   * whenever it opens — a serial left over from the last case closed would
   * be recorded against this one.
   */
  const [closeReaderSerial, setCloseReaderSerial] = useState('');
  const [closeSensorSerial, setCloseSensorSerial] = useState('');
  const [closeFailure, setCloseFailure] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const found = await casesApi.show(uuid);
      setRecord(found);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setFailureMessage(
        error.code === 'not_found' ? t('cases.notFound') : t('cases.loadFailedDetail'),
      );
      setStatus('error');
    }
  }, [uuid, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Mirrors `ExportButton.handleClick()`: `setPdfBusy(false)` is in a
  // `finally` so a failed download does not strand the button — and
  // `loading`, not `disabled`, is what `Button` reads, so a click while busy
  // is swallowed by `Button` itself (`handleClick` there `preventDefault`s
  // and returns) rather than needing a manual re-entrancy guard here.
  async function downloadPdf(): Promise<void> {
    setPdfBusy(true);
    setPdfError(null);

    try {
      await saveCasePdf(uuid);
    } catch {
      setPdfError(t('cases.downloadPdfFailed'));
    } finally {
      setPdfBusy(false);
    }
  }

  function closeCloseDialog(): void {
    if (closing) return;
    setCloseOpen(false);
    setCloseFailure(null);
  }

  async function confirmClose(): Promise<void> {
    if (record === null || closing) return;
    setClosing(true);
    setCloseFailure(null);

    try {
      const updated = await casesApi.close(record.uuid, {
        // Trimmed to `null` rather than sent as '': an empty string is a
        // value, and would store one where the operator meant "no device".
        replacement_reader_serial: closeReaderSerial.trim() === '' ? null : closeReaderSerial.trim(),
        replacement_sensor_serial: closeSensorSerial.trim() === '' ? null : closeSensorSerial.trim(),
      });
      setRecord(updated);
      setCloseOpen(false);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setCloseFailure(t('cases.closeFailed'));
    } finally {
      setClosing(false);
    }
  }

  function closeReopenDialog(): void {
    if (reopening) return;
    setReopenOpen(false);
    setReopenFailure(null);
  }

  async function confirmReopen(): Promise<void> {
    if (record === null || reopening) return;
    setReopening(true);
    setReopenFailure(null);

    try {
      const updated = await casesApi.reopen(record.uuid);
      setRecord(updated);
      setReopenOpen(false);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReopenFailure(t('cases.reopenFailed'));
    } finally {
      setReopening(false);
    }
  }

  function closeDeleteDialog(): void {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteFailure(null);
  }

  /**
   * ON SUCCESS THIS NAVIGATES AWAY, and that is not a nicety: the record
   * this screen is built on no longer exists, so every later fetch it makes
   * (`GET /cases/{case}`, the media gallery, the message thread) answers
   * 404. Staying put would leave an agent reading a case the system has
   * already forgotten.
   */
  async function confirmDelete(): Promise<void> {
    if (record === null || deleting) return;
    setDeleting(true);
    setDeleteFailure(null);

    try {
      await casesApi.remove(record.uuid);
      setDeleteOpen(false);
      onBack();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setDeleteFailure(t('cases.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }

  if (status === 'loading') {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('cases.back')}
        </Button>
        <p role="status">{t('list.loading')}</p>
      </div>
    );
  }

  if (status === 'error' || record === null) {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('cases.back')}
        </Button>
        <div role="alert" className={styles['recovery']}>
          <p>{failureMessage}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="caseDetailRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      </div>
    );
  }

  // A closed case offers neither edit nor close, regardless of which
  // permissions the session holds — `CaseController::update()`/`close()`
  // both refuse with a `409` once `isClosed()`; this screen never even
  // offers the control that would produce it.
  const isClosed = record.status === 'closed';
  const claimedCodes = (record.code_claims ?? []).filter((claim) => claim.claimed);

  return (
    <div className={styles['page']}>
      <div className={styles['header']}>
        <div className={styles['identity']}>
          <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
            {t('cases.back')}
          </Button>
          <h2 className={styles['title']}>
            <bdi dir="ltr">{record.case_number}</bdi>
          </h2>
        </div>

        <div className={styles['actions']}>
          <Button
            variant="ghost"
            loading={pdfBusy}
            onClick={() => {
              void downloadPdf();
            }}
            icon={<FilePdfIcon size={16} />}
          >
            {pdfBusy ? t('cases.downloadPdfInProgress') : t('cases.downloadPdf')}
          </Button>
          {!isClosed && canUpdate ? (
            <Button
              variant="secondary"
              onClick={() => {
                setEditOpen(true);
              }}
              icon={<PencilSimpleIcon size={16} />}
            >
              {t('cases.edit')}
            </Button>
          ) : null}
          {!isClosed && canClose ? (
            <Button
              onClick={() => {
                // Cleared on every open: a serial left from the last case
                // closed would otherwise be recorded against this one.
                setCloseReaderSerial('');
                setCloseSensorSerial('');
                setCloseOpen(true);
              }}
              icon={<LockSimpleIcon size={16} />}
            >
              {t('actions.closeCase')}
            </Button>
          ) : null}

          {/* The mirror of Close, and gated the opposite way: it appears
              only ONCE the case is closed, because that is the only state
              it is valid in — the server answers 409 for any other, and a
              control that can only fail is worse than no control. */}
          {isClosed && canReopen ? (
            <Button
              variant="secondary"
              onClick={() => {
                setReopenOpen(true);
              }}
              icon={<LockSimpleOpenIcon size={16} />}
            >
              {t('cases.reopen')}
            </Button>
          ) : null}

          {/* NOT gated on `isClosed`: a case closed by mistake is exactly
              one of the cases an administrator needs to be able to remove. */}
          {canDelete ? (
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteOpen(true);
              }}
              icon={<TrashIcon size={16} />}
            >
              {t('cases.delete')}
            </Button>
          ) : null}
        </div>
      </div>

      {pdfError === null ? null : (
        <p className={styles['pdfFailure']} role="alert" aria-atomic="true">
          {pdfError}
        </p>
      )}

      <dl className={[styles['panel'], styles['primary'], styles['fields']].join(' ')}>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.status')}</dt>
          <dd className={styles['fieldValue']}>{t(statusLabelKey(record.status))}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.mobile')}</dt>
          <dd className={styles['fieldValue']}>
            <bdi dir="ltr">{record.mobile}</bdi>
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.dispensary')}</dt>
          <dd className={styles['fieldValue']}>
            {optionalReferenceName(record.dispensary, i18n.language) ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.city')}</dt>
          <dd className={styles['fieldValue']}>{record.city?.name_ar ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.fieldCentre')}</dt>
          <dd className={styles['fieldValue']}>
            {optionalReferenceName(record.centre, i18n.language) ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.fieldPerceptionCode')}</dt>
          <dd className={styles['fieldValue']}>
            {record.perception_code ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.fieldAssessmentCode')}</dt>
          <dd className={styles['fieldValue']}>
            {record.assessment_code ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.fieldAgentNotes')}</dt>
          <dd className={styles['fieldValue']}>{record.agent_notes ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.readerSerial')}</dt>
          <dd className={styles['fieldValue']}>
            <bdi dir="ltr">{record.reader_serial ?? t('table.notRecorded')}</bdi>
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.readerType')}</dt>
          <dd className={styles['fieldValue']}>{record.reader_type ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.sensorSerial')}</dt>
          <dd className={styles['fieldValue']}>
            <bdi dir="ltr">{record.sensor_serial ?? t('table.notRecorded')}</bdi>
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.sensorType')}</dt>
          <dd className={styles['fieldValue']}>{record.sensor_type ?? t('table.notRecorded')}</dd>
        </div>
        {/*
          What the patient LEFT with. Written at close by the dialog below,
          and carried in from legacy `patients.newreaderSN`/`newsensorSN` for
          146,929 migrated cases — the only record of a replacement there is,
          since the legacy `replacements` table holds no rows at all.

          SHOWN UNCONDITIONALLY, like every field above it, rather than only
          when set. A pharmacist checking what was dispensed needs to see
          "Not recorded" and know it, not find the row missing and wonder
          whether this build shows it. `bdi dir="ltr"` for the same reason
          the two serials above have it: a serial is Latin text inside an
          Arabic page.
        */}
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.replacementReaderSerial')}</dt>
          <dd className={styles['fieldValue']}>
            <bdi dir="ltr">{record.replacement_reader_serial ?? t('table.notRecorded')}</bdi>
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('cases.replacementSensorSerial')}</dt>
          <dd className={styles['fieldValue']}>
            <bdi dir="ltr">{record.replacement_sensor_serial ?? t('table.notRecorded')}</bdi>
          </dd>
        </div>
      </dl>

      <CaseMessages caseUuid={record.uuid} canSend={canSend} canResend={canSend && !isClosed} />

      <CaseRequests
        caseNumber={record.case_number}
        canView={canViewRequests}
        onOpenRequest={onOpenRequest}
      />

      {/*
       * Viewing is NEVER gated — not by `isClosed`, not by a permission —
       * see `CaseMedia.tsx`'s own docblock: `GET /cases/{case}/media`
       * carries the same `can:viewAny` this screen already required to
       * load `record` at all. M-15c's review found the PDF download
       * hidden behind an `isClosed` gate for exactly this reason, and a
       * closed case's photos are the ones most likely to be wanted for
       * the physical file. Only uploading is gated, on `case.update`.
       */}
      <CaseMedia caseUuid={record.uuid} canUpload={canUpdate} />

      <CaseEscalations caseNumber={record.case_number} canView={canViewEscalations} />

      <CaseReplacements caseNumber={record.case_number} canView={canViewReplacements} />

      {/*
        Heading and list in ONE panel. They were two loose blocks on the page
        ground before, which is why the heading read as a label floating over
        the background image rather than as the title of the rows under it.

        ONLY CLAIMED CODES, and no panel at all when there are none: a line
        that got no code is not something the case record needs to announce,
        the same rule the create dialog's result panel follows.
      */}
      {claimedCodes.length === 0 ? null : (
        <section className={[styles['panel'], styles['claims']].join(' ')}>
          <CardHead level="h3" icon={<TicketIcon size={16} weight="duotone" />} title={t('cases.claimedCodesTitle')} />
          <ul className={styles['claimList']}>
            {claimedCodes.map((claim) => (
              <li key={`${claim.kind}-${claim.type_code}`} className={styles['claimRow']}>
                <span className={styles['claimKind']}>{t(`codes.kind.${claim.kind}`)}</span>
                <bdi dir="ltr" className={styles['claimCode']}>
                  {claim.code}
                </bdi>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CaseUpdateDialog
        open={editOpen}
        record={record}
        onClose={() => {
          setEditOpen(false);
        }}
        onSaved={(updated) => {
          setRecord(updated);
          setEditOpen(false);
        }}
      />

      <Dialog
        open={closeOpen}
        destructive
        title={t('dialog.closeCaseTitle', { caseNumber: record.case_number })}
        onClose={closeCloseDialog}
        actions={
          <>
            <Button
              variant="destructive"
              loading={closing}
              onClick={() => {
                void confirmClose();
              }}
              icon={<LockSimpleIcon size={16} />}
            >
              {t('actions.closeCase')}
            </Button>
            <Button variant="ghost" onClick={closeCloseDialog} icon={<XIcon size={16} />}>
              {t('cases.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['closeBody']}>{t('dialog.closeCaseBody')}</p>

        {/*
          THE DEVICE HANDED OVER, recorded as the case closes — the same
          moment `pharmacist-update-case.php` writes `newreaderSN` /
          `newsensorSN`, and the outcome the whole case exists to produce.

          Both optional: 23% of legacy's closed cases record no device at
          all, and requiring one would push an operator into typing a
          placeholder, which reads as a real serial forever afterwards.
        */}
        <div className={styles['closeDevices']}>
          <Input
            label={t('cases.replacementSensorSerial')}
            width="code"
            value={closeSensorSerial}
            onChange={(event) => {
              setCloseSensorSerial(event.target.value);
            }}
          />
          <Input
            label={t('cases.replacementReaderSerial')}
            width="code"
            value={closeReaderSerial}
            onChange={(event) => {
              setCloseReaderSerial(event.target.value);
            }}
          />
        </div>
        <div className={styles['closeFailure']} role="alert" aria-atomic="true">
          {closeFailure}
        </div>
      </Dialog>

      {/*
        A CONFIRMATION, like Close and Delete. Re-opening is not destructive,
        but it is not private either: the case reappears in every queue, every
        dashboard and every open-case count a supervisor reads, and it writes
        an audit row saying who revived it. Not `destructive` — that styling
        is the vocabulary for losing something, and this loses nothing.
      */}
      <Dialog
        open={reopenOpen}
        title={t('dialog.reopenCaseTitle', { caseNumber: record.case_number })}
        onClose={closeReopenDialog}
        actions={
          <>
            <Button
              loading={reopening}
              onClick={() => {
                void confirmReopen();
              }}
              icon={<LockSimpleOpenIcon size={16} />}
            >
              {t('cases.reopen')}
            </Button>
            <Button variant="ghost" onClick={closeReopenDialog} icon={<XIcon size={16} />}>
              {t('cases.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['closeBody']}>{t('dialog.reopenCaseBody')}</p>
        <div className={styles['closeFailure']} role="alert" aria-atomic="true">
          {reopenFailure}
        </div>
      </Dialog>

      <Dialog
        open={deleteOpen}
        destructive
        title={t('dialog.deleteCaseTitle', { caseNumber: record.case_number })}
        onClose={closeDeleteDialog}
        actions={
          <>
            <Button
              variant="destructive"
              loading={deleting}
              onClick={() => {
                void confirmDelete();
              }}
              icon={<TrashIcon size={16} />}
            >
              {t('cases.delete')}
            </Button>
            <Button variant="ghost" onClick={closeDeleteDialog} icon={<XIcon size={16} />}>
              {t('cases.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['closeBody']}>{t('dialog.deleteCaseBody')}</p>

        {/*
          THE VOUCHER SENTENCE, AND IT ONLY APPEARS WHEN THERE ARE VOUCHERS.
          The server releases the case's claimed codes back into the pool as
          part of the same transaction; that is a consequence nobody would
          predict from the word "delete", and it is the one an administrator
          most needs to have been told about BEFORE the click, not after.
          `code_claims` is present on `GET /cases/{uuid}` and absent from the
          list rows (see `cases.ts`), so `undefined` is "not known", never
          "none" — hence the length check rather than a truthiness test.
        */}
        {record.code_claims !== undefined && record.code_claims.length > 0 ? (
          <p className={styles['closeBody']}>
            {t('dialog.deleteCaseCodes', { count: record.code_claims.length })}
          </p>
        ) : null}

        <div className={styles['closeFailure']} role="alert" aria-atomic="true">
          {deleteFailure}
        </div>
      </Dialog>
    </div>
  );
}
